import {
  TASK_TYPE_LABELS, taskTypeBadgeClass, TASK_DOMAIN_LABELS, taskDomainBadgeClass, PRIORITY_LABELS, priorityBadgeClass, TASK_STATUS_LABELS, taskStatusBadgeClass, taskStatusDotClass, taskDisplayStatus,
} from "../../lib/tasks";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "../ui/responsive-dropdown-menu";
import { Building2, MoreHorizontal, Pencil, Trash2, UserRoundCog, Plus } from "lucide-react";
import AssigneeAvatars from "../AssigneeAvatars";
import { formatMobileDue } from "../TaskTimeline";
import { formatDueDate } from "../TaskDetailSheet";

// Desktop renders a table (`hidden md:block`); mobile gets its own card list
// (`md:hidden`), since a table with most columns hidden is not a usable
// mobile view. Both share the same `paged` array, actions and permissions.
export function ListView({
  paged, canEdit, canDelete, directory, resolveAssignee, resolveProspectName, navigate,
  onCycleStatus, onToggleDone, onEdit, onDelete, onAddAssignee, onView,
  total, pageSize, currentPage, totalPages, onPageChange,
}) {
  return (
    <>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tâche</TableHead>
              <TableHead className="hidden md:table-cell">Prospect</TableHead>
              <TableHead className="hidden lg:table-cell">Type d'action</TableHead>
              <TableHead className="hidden lg:table-cell">Domaine</TableHead>
              <TableHead>Échéance</TableHead>
              <TableHead className="hidden lg:table-cell">Priorité</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="hidden lg:table-cell">Responsable</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((t) => (
              <TaskRow
                key={t.id} task={t} canEdit={canEdit} canDelete={canDelete}
                directory={directory} resolveAssignee={resolveAssignee} navigate={navigate}
                onCycleStatus={onCycleStatus} onToggleDone={onToggleDone} onEdit={onEdit} onDelete={onDelete} onAddAssignee={onAddAssignee}
                onView={onView}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="md:hidden p-2 space-y-2">
        {paged.map((t) => (
          <MobileTaskRow
            key={t.id} task={t} canEdit={canEdit} canDelete={canDelete}
            directory={directory} resolveAssignee={resolveAssignee} resolveProspectName={resolveProspectName}
            navigate={navigate} onToggleDone={onToggleDone} onEdit={onEdit} onDelete={onDelete}
            onAddAssignee={onAddAssignee} onView={onView}
          />
        ))}
      </div>
      <PaginationFooter total={total} pageSize={pageSize} currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
    </>
  );
}

// Mobile card: the title is the tappable element (opens TaskDetailSheet);
// the checkbox and the ⋯ menu are separate sibling controls, so neither can
// trigger it. The checkbox reuses onToggleDone: checking a_faire/en_cours
// moves straight to termine, unchecking termine goes back to a_faire.
export function MobileTaskRow({ task, canEdit, canDelete, directory, resolveAssignee, resolveProspectName, navigate, onToggleDone, onEdit, onDelete, onAddAssignee, onView }) {
  const display = taskDisplayStatus(task);
  const due = formatMobileDue(task.due_date, task.done);
  const domainLabel = task.domain ? TASK_DOMAIN_LABELS[task.domain] : "Sans domaine";
  const prospectName = task.prospect_id ? resolveProspectName(task.prospect_id) : null;

  return (
    <div data-testid={`mobile-task-${task.id}`} className="rounded-lg border border-slate-100 bg-white px-3 py-2.5">
      <div className="flex items-start gap-2">
        {canEdit ? (
          <Checkbox
            checked={task.done} onCheckedChange={() => onToggleDone(task)}
            data-testid={`mobile-toggle-${task.id}`} className="shrink-0 mt-0.5"
          />
        ) : (
          <span className="w-4 h-4 rounded-full border border-slate-300 shrink-0 mt-0.5" />
        )}
        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${taskStatusDotClass(display)}`} />
        <button
          type="button" onClick={() => onView(task)} data-testid={`mobile-view-task-${task.id}`}
          className={`flex-1 min-w-0 text-sm font-semibold text-left ${task.done ? "line-through text-slate-400" : "text-slate-900"}`}
        >
          {task.title}
        </button>
        {due && (
          <span className={`text-xs shrink-0 ${due.overdue ? "text-red-600 font-medium" : "text-slate-500"}`}>{due.text}</span>
        )}
        {(canEdit || canDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button" className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500 shrink-0"
                data-testid={`mobile-actions-${task.id}`}
              >
                <MoreHorizontal size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && (
                <DropdownMenuItem onClick={() => onEdit(task)}>
                  <Pencil size={14} className="mr-2" /> Modifier
                </DropdownMenuItem>
              )}
              {canEdit && directory.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  {directory.filter((u) => !(task.assigned_to || []).includes(u.id)).map((u) => (
                    <DropdownMenuItem key={u.id} onClick={() => onAddAssignee(task, u.id)}>
                      <UserRoundCog size={14} className="mr-2" /> Ajouter {u.name} comme responsable
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(task)} className="text-red-600 focus:text-red-600">
                    <Trash2 size={14} className="mr-2" /> Supprimer
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="pl-4 mt-1 flex items-center gap-2 flex-wrap text-xs text-slate-500">
        <span className="truncate">{domainLabel}</span>
        <Badge variant="outline" className={`border-transparent text-2xs font-medium shrink-0 ${priorityBadgeClass(task.priority)}`}>
          {PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium}
        </Badge>
      </div>
      {task.prospect_id && (
        <div className="pl-4 mt-1">
          <button
            type="button" onClick={() => navigate(`/prospects/${task.prospect_id}`)}
            className="inline-flex items-center gap-1 text-xs text-jamora hover:underline max-w-[220px] truncate"
          >
            <Building2 size={12} className="shrink-0" /> {prospectName || "Prospect lié"}
          </button>
        </div>
      )}
      <div className="pl-4 mt-1.5">
        <AssigneeAvatars userIds={task.assigned_to} resolveUser={resolveAssignee} />
      </div>
    </div>
  );
}

export function TaskRow({ task, canEdit, canDelete, directory, resolveAssignee, navigate, onCycleStatus, onToggleDone, onEdit, onDelete, onAddAssignee, onView }) {
  const due = formatDueDate(task.due_date, task.done);
  const display = taskDisplayStatus(task);
  return (
    <TableRow data-testid={`task-row-${task.id}`}>
      <TableCell>
        <div className="flex items-start gap-2">
          {canEdit ? (
            <Checkbox
              checked={task.done} onCheckedChange={() => onToggleDone(task)}
              data-testid={`toggle-done-${task.id}`} className="mt-1 shrink-0"
            />
          ) : (
            <span className="w-4 h-4 rounded-full border border-slate-300 shrink-0 mt-1" />
          )}
          <div className="min-w-0">
            <button
              type="button" onClick={() => onView(task)} data-testid={`view-task-${task.id}`}
              className={`text-sm font-medium text-left hover:underline ${task.done ? "line-through text-slate-400" : "text-slate-900"}`}
            >
              {task.title}
            </button>
            {task.description && <div className="text-xs text-slate-500 truncate max-w-xs">{task.description}</div>}
            {/* The Priorité column is hidden below lg, so it is shown inline here
                on those breakpoints only: a task's priority is never invisible on
                mobile, without a second row layout. */}
            <div className="mt-1 lg:hidden">
              <Badge variant="outline" className={`border-transparent text-2xs font-medium ${priorityBadgeClass(task.priority)}`}>{PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium}</Badge>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {task.prospect_id ? (
          <button type="button" onClick={() => navigate(`/prospects/${task.prospect_id}`)} className="text-sm text-jamora hover:underline">
            Voir le prospect
          </button>
        ) : <span className="text-sm text-slate-400">Aucun</span>}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <Badge variant="outline" className={`border-transparent text-2xs font-medium ${taskTypeBadgeClass(task.task_type)}`}>{TASK_TYPE_LABELS[task.task_type] || TASK_TYPE_LABELS.autre}</Badge>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {task.domain ? (
          <Badge variant="outline" className={`border-transparent text-2xs font-medium ${taskDomainBadgeClass()}`}>{TASK_DOMAIN_LABELS[task.domain]}</Badge>
        ) : <span className="text-sm text-slate-400">Sans domaine</span>}
      </TableCell>
      <TableCell>
        {due ? <span className={`text-sm ${due.overdue ? "text-red-600 font-medium" : "text-slate-600"}`}>{due.text}</span> : <span className="text-sm text-slate-400">Aucune</span>}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <Badge variant="outline" className={`border-transparent text-2xs font-medium ${priorityBadgeClass(task.priority)}`}>{PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium}</Badge>
      </TableCell>
      <TableCell>
        {canEdit ? (
          <button
            type="button"
            onClick={() => onCycleStatus(task)}
            data-testid={`cycle-status-${task.id}`}
            className="inline-block"
            title="Cliquer pour passer au statut suivant"
          >
            <Badge variant="outline" className={`border-transparent text-2xs font-medium ${taskStatusBadgeClass(display)}`}>
              {display === "en_retard" ? "En retard" : TASK_STATUS_LABELS[display]}
            </Badge>
          </button>
        ) : (
          <Badge variant="outline" className={`border-transparent text-2xs font-medium ${taskStatusBadgeClass(display)}`}>
            {display === "en_retard" ? "En retard" : TASK_STATUS_LABELS[display]}
          </Badge>
        )}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <AssigneeAvatars userIds={task.assigned_to} resolveUser={resolveAssignee} />
      </TableCell>
      <TableCell className="text-right">
        {(canEdit || canDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`row-actions-${task.id}`}>
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && (
                <DropdownMenuItem onClick={() => onEdit(task)} data-testid={`edit-task-${task.id}`}>
                  <Pencil size={14} className="mr-2" /> Modifier
                </DropdownMenuItem>
              )}
              {canEdit && directory.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  {directory.filter((u) => !(task.assigned_to || []).includes(u.id)).map((u) => (
                    <DropdownMenuItem key={u.id} onClick={() => onAddAssignee(task, u.id)}>
                      <UserRoundCog size={14} className="mr-2" /> Ajouter {u.name} comme responsable
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(task)} className="text-red-600 focus:text-red-600" data-testid={`delete-task-${task.id}`}>
                    <Trash2 size={14} className="mr-2" /> Supprimer
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}

export function EmptyState({ hasActiveFilters, canCreate, onCreate, onReset }) {
  if (hasActiveFilters) {
    return (
      <div className="text-center py-12 px-4">
        <p className="text-sm text-slate-500">Aucune tâche ne correspond à ces filtres.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onReset}>Réinitialiser les filtres</Button>
      </div>
    );
  }
  return (
    <div className="text-center py-12 px-4">
      <p className="text-sm font-medium text-slate-700">Aucune tâche pour le moment</p>
      <p className="text-sm text-slate-500 mt-1">Ajoutez une tâche pour commencer à suivre vos actions.</p>
      {canCreate && (
        <Button className="mt-4 gap-2" onClick={onCreate}>
          <Plus size={16} /> Nouvelle tâche
        </Button>
      )}
    </div>
  );
}

export function PaginationFooter({ total, pageSize, currentPage, totalPages, onPageChange }) {
  const start = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
      <span className="text-xs text-slate-500">{start}-{end} sur {total} tâche{total > 1 ? "s" : ""}</span>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>Précédent</Button>
        <span className="text-xs text-slate-500 whitespace-nowrap">{currentPage} / {totalPages}</span>
        <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>Suivant</Button>
      </div>
    </div>
  );
}
